import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AvailableField, PricingNodeData } from "@/lib/productGraph";
import type {
  InstallmentTier,
  PricingFormula,
  RateTableEntry,
} from "@/services/platformEnterprisesService";

interface Props {
  data: PricingNodeData;
  onChange: (patch: Partial<PricingNodeData>) => void;
  availableFields: AvailableField[];
}

const NONE = "__none__";

function patchFormula(
  current: PricingFormula,
  patch: Partial<PricingFormula>,
): PricingFormula {
  return { ...current, ...patch };
}

export function PricingEditor({ data, onChange, availableFields }: Props): JSX.Element {
  const { t } = useTranslation();
  const formula = data.formula;

  const numberFields = availableFields.filter((f) => f.type === "number");
  const selectFields = availableFields.filter((f) => f.type === "select");
  const installmentCandidates = availableFields.filter(
    (f) => f.type === "number" || f.type === "select",
  );
  const profile = availableFields.find((f) => f.key === formula.profileField);
  const profileOptions = profile?.options ?? [];

  function setF(patch: Partial<PricingFormula>): void {
    onChange({ formula: patchFormula(formula, patch) });
  }

  function toggleBaseField(key: string): void {
    const has = formula.baseFields.includes(key);
    setF({
      baseFields: has
        ? formula.baseFields.filter((k) => k !== key)
        : [...formula.baseFields, key],
    });
  }

  function addRate(): void {
    const empty: RateTableEntry = {
      profileValue: profileOptions[0]?.value ?? "",
      profileLabel: profileOptions[0]?.label ?? "",
      annualRatePercent: 0,
    };
    setF({ rates: [...formula.rates, empty] });
  }

  function updateRate(idx: number, patch: Partial<RateTableEntry>): void {
    setF({ rates: formula.rates.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  }

  function removeRate(idx: number): void {
    setF({ rates: formula.rates.filter((_, i) => i !== idx) });
  }

  function addTier(): void {
    const tier: InstallmentTier = { installments: 1, label: "", multiplier: 1 };
    setF({ installmentTiers: [...(formula.installmentTiers ?? []), tier] });
  }

  function updateTier(idx: number, patch: Partial<InstallmentTier>): void {
    setF({
      installmentTiers: (formula.installmentTiers ?? []).map((t, i) =>
        i === idx ? { ...t, ...patch } : t,
      ),
    });
  }

  function removeTier(idx: number): void {
    setF({
      installmentTiers: (formula.installmentTiers ?? []).filter((_, i) => i !== idx),
    });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">{t("enterpriseProductBuilder.nodeLabelLabel")}</Label>
        <Input
          value={data.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.pricingResultLabel")}
        </Label>
        <Input
          value={formula.resultLabel ?? ""}
          onChange={(e) => setF({ resultLabel: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {t("enterpriseProductBuilder.pricingBaseFieldsLabel")}
        </Label>
        {numberFields.length === 0 ? (
          <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
            {t("enterpriseProductBuilder.noNumberFieldsAvailable")}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {numberFields.map((f) => {
              const checked = formula.baseFields.includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => toggleBaseField(f.key)}
                  className={
                    "rounded-md border px-2 py-1 text-xs transition-colors " +
                    (checked
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-input bg-background")
                  }
                >
                  {f.label || f.key}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.pricingProfileFieldLabel")}
          </Label>
          <Select
            value={formula.profileField || NONE}
            onValueChange={(v) => setF({ profileField: v === NONE ? "" : v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>—</SelectItem>
              {selectFields.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.label || f.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.pricingInstallmentFieldLabel")}
          </Label>
          <Select
            value={formula.installmentField || NONE}
            onValueChange={(v) =>
              setF({ installmentField: v === NONE ? undefined : v })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>—</SelectItem>
              {installmentCandidates.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.label || f.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("enterpriseProductBuilder.pricingRatesLabel")}
          </Label>
          <Button size="sm" variant="outline" onClick={addRate}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("enterpriseProductBuilder.addRate")}
          </Button>
        </div>
        {formula.rates.length === 0 && (
          <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
            {t("enterpriseProductBuilder.noRatesYet")}
          </p>
        )}
        {formula.rates.map((rate, idx) => (
          <div
            key={idx}
            className="grid grid-cols-[1fr,1fr,90px,32px] gap-2 rounded-md border bg-muted/30 p-2"
          >
            <Select
              value={rate.profileValue || NONE}
              onValueChange={(v) => {
                const opt = profileOptions.find((o) => o.value === v);
                updateRate(idx, {
                  profileValue: v === NONE ? "" : v,
                  profileLabel: opt?.label ?? "",
                });
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder={t("enterpriseProductBuilder.profileValue")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {profileOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={rate.profileLabel}
              onChange={(e) => updateRate(idx, { profileLabel: e.target.value })}
              placeholder={t("enterpriseProductBuilder.profileLabel")}
              className="h-8 text-xs"
            />
            <Input
              type="number"
              value={rate.annualRatePercent}
              onChange={(e) =>
                updateRate(idx, { annualRatePercent: Number(e.target.value) })
              }
              className="h-8 text-xs"
            />
            <Button
              size="sm"
              variant="ghost-destructive"
              className="h-8 w-8 p-0"
              onClick={() => removeRate(idx)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      {formula.installmentField && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              {t("enterpriseProductBuilder.pricingTiersLabel")}
            </Label>
            <Button size="sm" variant="outline" onClick={addTier}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t("enterpriseProductBuilder.addTier")}
            </Button>
          </div>
          {(formula.installmentTiers ?? []).length === 0 && (
            <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
              {t("enterpriseProductBuilder.noTiersYet")}
            </p>
          )}
          {(formula.installmentTiers ?? []).map((tier, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[80px,1fr,90px,32px] gap-2 rounded-md border bg-muted/30 p-2"
            >
              <Input
                type="number"
                min={1}
                value={tier.installments}
                onChange={(e) =>
                  updateTier(idx, { installments: Number(e.target.value) })
                }
                className="h-8 text-xs"
              />
              <Input
                value={tier.label}
                onChange={(e) => updateTier(idx, { label: e.target.value })}
                placeholder={t("enterpriseProductBuilder.tierLabel")}
                className="h-8 text-xs"
              />
              <Input
                type="number"
                step="0.01"
                value={tier.multiplier}
                onChange={(e) =>
                  updateTier(idx, { multiplier: Number(e.target.value) })
                }
                className="h-8 text-xs"
              />
              <Button
                size="sm"
                variant="ghost-destructive"
                className="h-8 w-8 p-0"
                onClick={() => removeTier(idx)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
